import { useState, useEffect } from 'react'
import type { SelectOption } from '../../../components/AutoJobs/MBLHBLTable'
import SearchableSelect from '../../../components/SearchableSelect'
import PortLocodeWarning from '../../../components/PortLocodeWarning'
import { supabase } from '../../../api/supabase'
import { findCode, findMatch, uniqueMatch } from './portCarrierMatch'
import { BOXTOP_SYNC_ENABLED } from '../../../lib/boxtopSync'

interface ExtractedData {
  cargo?: { gross_weight_kgs?: number; volume_cbm?: number; pieces?: number }
  container?: { packages?: string }
  carrier?: string
  shipping_terms?: string
  freight_type?: string
  ports?: { loading?: string; discharge?: string; delivery?: string }
  etd?: string
  eta?: string
  [key: string]: any
}

interface CodeOption {
  id?: string
  code: string | null
  name: string
  type?: string | null
  un_locode?: string | null
}

interface CustomerOption {
  id: string
  code: string
  name: string
}

// Task J/Y2: values the user sets here apply to every HBL row on creation because
// create-autojobs' per-child fallbacks (detail.field || master.field) all bottom
// out on these same master keys -- shipping line has no per-child override at all,
// and departure/arrival date are only ever read from master.etd/master.eta.
export interface JobCreationPrefill {
  carrier?: string
  carrierCode?: string
  ports?: { loading?: string; delivery?: string }
  // Z37: type-aware -- for a SEA job this is the 5-char UN/LOCODE (ports.un_locode), for an AIR
  // job the 3-letter IATA code (ports.code). create-autojobs' lookupOrCreatePortId() picks
  // which column to match against by length, same heuristic it already used for a code
  // embedded in a parsed name.
  portLoadingCode?: string
  portDeliveryCode?: string
  // Z32: set only when the staff member picked an exact match via the SearchableSelect below
  // -- create-autojobs uses it directly instead of running lookupOrCreatePortId's fuzzy
  // name/code match, so a confirmed selection can't be second-guessed by that heuristic.
  portLoadingId?: string
  portDeliveryId?: string
  etd?: string
  eta?: string
  created_by?: string
  shed_id?: string
  storage_location?: string
  invoiceToId?: string
  overseasAgentId?: string
  portAgentId?: string
}

interface JobCreationConfirmDialogProps {
  mode: string
  parentRef: string
  master: ExtractedData
  details: ExtractedData[]
  customersCreatedCount: number
  // Y2: raw carrier/port lists so the dialog can auto-fill each Code field from whichever
  // name is typed/parsed.
  carriers?: CodeOption[]
  ports?: CodeOption[]
  // Y-followup (task 22, bug F): Invoice To / Overseas Agent / Port Agent dropdowns
  customers?: CustomerOption[]
  userOptions?: string[]
  currentUserEmail?: string
  shedOptions?: SelectOption[]
  onConfirm: (prefill: JobCreationPrefill) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

// Z37: SEAPORT's authoritative identifier is the UN/LOCODE, AIRPORT's is the 3-letter IATA
// `code` (Z33's ports-admin/dropdown convention -- ports.code is often unset for SEAPORT rows
// now that it's derived rather than staff-entered there). Never falls back to the other
// identifier when the matched row is missing its own -- that's a ports-table data gap worth
// surfacing, not silently working around with the wrong-typed value.
function portIdentifier(match: CodeOption | undefined, isSeaport: boolean): string {
  if (!match) return ''
  const value = isSeaport ? match.un_locode : match.code
  if (!value) {
    console.warn(`[JobCreationConfirmDialog] Port "${match.name}" has no ${isSeaport ? 'UN/LOCODE' : 'IATA code'} on file`)
    return ''
  }
  return value
}

// Z38: three states staff can see at a glance for a port/carrier field -- 'matched' (found and
// has its identifier, nothing to do), 'needs_code' (found but the row is missing its
// identifier -- fill it in and it's saved straight to that row), 'new' (no match at all -- it's
// learned into the table via the existing lookupOrCreatePortId/lookupCarrierId when the job is
// actually created). null means there's no name to match against yet.
type MatchStatus = 'matched' | 'needs_code' | 'new'
function matchStatus(name: string, id: string, identifier: string): MatchStatus | null {
  if (!name.trim()) return null
  if (!id) return 'new'
  return identifier ? 'matched' : 'needs_code'
}

const STATUS_BADGE: Record<MatchStatus, { label: string; cls: string }> = {
  matched: { label: 'Auto-matched', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  needs_code: { label: 'Needs code', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  new: { label: 'Will create new', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
}
function StatusBadge({ status }: { status: MatchStatus | null }) {
  if (!status) return null
  const { label, cls } = STATUS_BADGE[status]
  return <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${cls}`}>{label}</span>
}

export default function JobCreationConfirmDialog({
  mode,
  parentRef,
  master,
  details,
  customersCreatedCount,
  carriers = [],
  ports = [],
  customers = [],
  userOptions = [],
  currentUserEmail = '',
  shedOptions = [],
  onConfirm,
  onCancel,
  isLoading = false,
}: JobCreationConfirmDialogProps) {
  const [syncToBoxtrax, setSyncToBoxtrax] = useState(false)
  // Z15(C): Invoice To / Overseas Agent are required -- only shown once the user has
  // tried to submit, so the dialog doesn't open already covered in red.
  const [confirmAttempted, setConfirmAttempted] = useState(false)

  // Z32: same AIR/SEA -> AIRPORT/SEAPORT rule create-autojobs uses server-side (parentPortType)
  // -- Z27 scoped ports.code to be unique per type, so a AIR job must only ever match/create
  // an AIRPORT row. Computed up front (not just where portOptions was before) since Z37's
  // type-aware code-field initializers below need it too.
  const portType = master.transport_mode === 'AIR' ? 'AIRPORT' : 'SEAPORT'
  const isSeaport = portType === 'SEAPORT'
  // Z41: scope port matches to the job's own port type (a SEA job must only ever match/create
  // a SEAPORT row) and, among same-named rows, prefer whichever already carries this type's
  // identifier -- a stub row created before its LOCODE/code was known shouldn't beat the
  // canonical one. Carriers have no type to scope by, just the code preference.
  const portMatchOpts = { type: portType, identifierKey: isSeaport ? 'un_locode' as const : 'code' as const }
  const carrierMatchOpts = { identifierKey: 'code' as const }

  // Pre-fill fields -- "empty = don't set" means the field is left out of the
  // prefill payload entirely on confirm, so master's already-parsed value (if any)
  // survives untouched rather than being blanked out.
  // Z41.3: one-way only -- LEFT is always the free-text parsed name (editable if staff
  // corrects it), RIGHT is auto-fetched from a debounced lookup against that name. No
  // dropdown, no lookup-by-code, no "multiple matches" picker -- an ambiguous or absent
  // match just falls through to the same "will create new" state as a genuine miss.
  const [shippingLine, setShippingLine] = useState(master.carrier || '')
  const [carrierCode, setCarrierCode] = useState(() => findCode(carriers, master.carrier, carrierMatchOpts))
  const [departurePort, setDeparturePort] = useState(master.ports?.loading || '')
  const [departurePortCode, setDeparturePortCode] = useState(() => portIdentifier(uniqueMatch(ports, master.ports?.loading, portMatchOpts), isSeaport))
  const [arrivalPort, setArrivalPort] = useState(master.ports?.delivery || '')
  const [arrivalPortCode, setArrivalPortCode] = useState(() => portIdentifier(uniqueMatch(ports, master.ports?.delivery, portMatchOpts), isSeaport))
  // Resolved port row behind each free-text name field -- drives the △ missing-code/LOCODE
  // warning and, below, the debounced auto-fill of the code field.
  const [departurePortMatch, setDeparturePortMatch] = useState(() => uniqueMatch(ports, master.ports?.loading, portMatchOpts))
  const [arrivalPortMatch, setArrivalPortMatch] = useState(() => uniqueMatch(ports, master.ports?.delivery, portMatchOpts))
  // Exact port row resolved by the name lookup. Empty means "no unique match" --
  // create-autojobs falls back to lookupOrCreatePortId(name, code) exactly as before.
  const [departurePortId, setDeparturePortId] = useState(() => departurePortMatch?.id || '')
  const [arrivalPortId, setArrivalPortId] = useState(() => arrivalPortMatch?.id || '')

  // 300ms after staff stops typing a name, re-run the match and refresh the code field --
  // debounced so the RIGHT field doesn't flicker mid-keystroke. Matching stays client-side
  // against the already-loaded `ports`/`carriers` props (normalizeMatchName already strips a
  // trailing ", COUNTRY" and does the ILIKE-equivalent substring/fuzzy cascade) rather than a
  // live per-keystroke query -- the full active list is already on hand, so there's nothing a
  // round trip would buy here.
  useEffect(() => {
    const t = setTimeout(() => {
      const match = uniqueMatch(ports, departurePort, portMatchOpts)
      setDeparturePortMatch(match || findMatch(ports, departurePort, portMatchOpts))
      setDeparturePortId(match?.id || '')
      setDeparturePortCode(portIdentifier(match, isSeaport))
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departurePort])

  useEffect(() => {
    const t = setTimeout(() => {
      const match = uniqueMatch(ports, arrivalPort, portMatchOpts)
      setArrivalPortMatch(match || findMatch(ports, arrivalPort, portMatchOpts))
      setArrivalPortId(match?.id || '')
      setArrivalPortCode(portIdentifier(match, isSeaport))
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivalPort])

  useEffect(() => {
    const t = setTimeout(() => {
      setCarrierCode(findCode(carriers, shippingLine, carrierMatchOpts))
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingLine])

  const [departureDate, setDepartureDate] = useState('')
  const [arrivalDate, setArrivalDate] = useState('')
  const [createdBy, setCreatedBy] = useState(currentUserEmail)
  const [shedId, setShedId] = useState('')
  const [storageLocation, setStorageLocation] = useState('')
  const [invoiceToId, setInvoiceToId] = useState('')
  const [overseasAgentId, setOverseasAgentId] = useState('')
  const [portAgentId, setPortAgentId] = useState('')

  // Z33/Z37: SEAPORT's identifier is the UN/LOCODE (e.g. GBFXT), not the 3-letter `code` --
  // showing `code` for seaports is ambiguous now that it's only unique per type (Z27/Z29).
  // AIRPORT still shows its IATA `code`; LOCODE is optional/unused there.

  // Z38: "found match but missing code" -- staff fills in the gap right here and it's saved
  // straight to that ports row (progressive learning), rather than waiting on job creation's
  // own lookupOrCreatePortId (which, given we already have an exact id, would never even run --
  // see the portLoadingId/portDeliveryId fast path in create-autojobs). A no-op unless we
  // actually have both a matched id and a non-empty value to save.
  // ports/carriers UPDATE is RLS-gated to ADMIN only (20260507_enable_rls_all_tables.sql) -- a
  // non-admin's fix here won't error, it'll just match 0 rows, so .select() is needed to tell
  // "saved" from "silently blocked" and at least log the difference (job creation itself is
  // unaffected either way -- create-autojobs writes via the service role, not this call).
  async function syncPortIdentifier(portId: string, value: string) {
    if (!portId || !value) return
    const column = isSeaport ? 'un_locode' : 'code'
    const { data, error } = await supabase.from('ports').update({ [column]: value }).eq('id', portId).select('id')
    if (error) console.error('[JobCreationConfirmDialog] Failed to save port identifier:', error)
    else if (!data || data.length === 0) console.warn('[JobCreationConfirmDialog] Port identifier not saved (0 rows) -- likely needs ADMIN role to edit the ports table')
  }

  async function syncCarrierCode(carrierId: string, value: string) {
    if (!carrierId || !value) return
    const { data, error } = await supabase.from('carriers').update({ code: value }).eq('id', carrierId).select('id')
    if (error) console.error('[JobCreationConfirmDialog] Failed to save carrier code:', error)
    else if (!data || data.length === 0) console.warn('[JobCreationConfirmDialog] Carrier code not saved (0 rows) -- likely needs ADMIN role to edit the carriers table')
  }

  // Not stored in state -- recomputed each render from the current shippingLine text, same as
  // the port matches above.
  const carrierMatch = findMatch(carriers, shippingLine, carrierMatchOpts)
  const departureStatus = matchStatus(departurePort, departurePortId, departurePortCode)
  const arrivalStatus = matchStatus(arrivalPort, arrivalPortId, arrivalPortCode)
  const carrierStatus = matchStatus(shippingLine, carrierMatch?.id || '', carrierCode)
  const hasMissingCodes = departureStatus === 'needs_code' || arrivalStatus === 'needs_code' || carrierStatus === 'needs_code'

  function buildPrefill(): JobCreationPrefill {
    const prefill: JobCreationPrefill = {}
    if (shippingLine) prefill.carrier = shippingLine
    if (carrierCode) prefill.carrierCode = carrierCode
    if (departurePort || arrivalPort) {
      prefill.ports = {}
      if (departurePort) prefill.ports.loading = departurePort
      if (arrivalPort) prefill.ports.delivery = arrivalPort
    }
    if (departurePortCode) prefill.portLoadingCode = departurePortCode
    if (arrivalPortCode) prefill.portDeliveryCode = arrivalPortCode
    if (departurePortId) prefill.portLoadingId = departurePortId
    if (arrivalPortId) prefill.portDeliveryId = arrivalPortId
    if (departureDate) prefill.etd = departureDate
    if (arrivalDate) prefill.eta = arrivalDate
    if (createdBy) prefill.created_by = createdBy
    if (shedId) prefill.shed_id = shedId
    if (storageLocation) prefill.storage_location = storageLocation
    if (invoiceToId) prefill.invoiceToId = invoiceToId
    if (overseasAgentId) prefill.overseasAgentId = overseasAgentId
    if (portAgentId) prefill.portAgentId = portAgentId
    return prefill
  }

  function handleConfirmClick() {
    setConfirmAttempted(true)
    if (!invoiceToId || !overseasAgentId) return
    onConfirm(buildPrefill())
  }

  const totalPackages = details.length > 0
    ? details.reduce((sum, d) => sum + (parseInt(d.container?.packages || '0') || d.cargo?.pieces || 0), 0)
    : (parseInt(master.container?.packages || '0') || master.cargo?.pieces || 0)

  const totalWeight = details.length > 0
    ? details.reduce((sum, d) => sum + (d.cargo?.gross_weight_kgs || 0), 0)
    : (master.cargo?.gross_weight_kgs || 0)

  const totalVolume = details.length > 0
    ? details.reduce((sum, d) => sum + (d.cargo?.volume_cbm || 0), 0)
    : (master.cargo?.volume_cbm || 0)

  const isSingleMode = ['single', 'mbl_hbl'].includes(mode)
  const hasChildren = ['bundled', 'manifest', 'invoices'].includes(mode)
  const childCount = hasChildren ? details.length : 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Confirm Job Creation</h2>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Parent Job Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Job References</p>
            <p className="text-lg font-semibold text-blue-700 dark:text-blue-400">
              Will be assigned upon creation
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {isSingleMode
                ? '📌 Format: KI/KE-5digit'
                : `📌 Parent: KI/KE-5digit | Children: Parent-01, -02, ... -${childCount.toString().padStart(2, '0')}`}
            </p>
          </div>

          {/* Job Preview */}
          <div className="space-y-2">
            <p className="font-semibold text-gray-900 dark:text-white text-sm">Jobs to Create</p>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm text-gray-700 dark:text-gray-300 space-y-1">
              {isSingleMode ? (
                <p>📄 1 Single Job (MBL + HBL)</p>
              ) : (
                <>
                  <p>📦 1 Parent Job</p>
                  {hasChildren && (
                    <p className="text-blue-600 dark:text-blue-400">
                      ├─ {childCount} Child Jobs (HBLs)
                      {parentRef && (
                        <>
                          {childCount > 5 && (
                            <span className="block text-xs text-gray-600 dark:text-gray-400 ml-2">
                              {`(showing first 5: ${details
                                .slice(0, 5)
                                .map((_, i) => `${parentRef}-${String(i + 1).padStart(2, '0')}`)
                                .join(', ')} ... and ${childCount - 5} more)`}
                            </span>
                          )}
                          {childCount <= 5 && (
                            <span className="block text-xs text-gray-600 dark:text-gray-400 ml-2">
                              {details
                                .map((_, i) => `${parentRef}-${String(i + 1).padStart(2, '0')}`)
                                .join(', ')}
                            </span>
                          )}
                        </>
                      )}
                      {!parentRef && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400 ml-2">
                          (refs will be assigned upon creation)
                        </span>
                      )}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalPackages}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Packages</p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalWeight.toFixed(0)}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Weight (kg)</p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalVolume.toFixed(2)}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Volume (m³)</p>
            </div>
          </div>

          {/* Customers Info */}
          {customersCreatedCount > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg text-sm">
              <p className="text-amber-800 dark:text-amber-300">
                ℹ️ {customersCreatedCount} new customer(s) will be created
              </p>
            </div>
          )}

          {/* Y2: pre-fill fields applied to master + all HBL rows on creation. Freight
              Terms/Type were dropped here -- Gary: each job varies and the AI reader
              already fills them in per-row, so a shared "apply to all" default was
              actively wrong more often than it helped. */}
          <div className="space-y-3">
            <p className="font-semibold text-gray-900 dark:text-white text-sm">Pre-fill Options</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                  Carrier <StatusBadge status={carrierStatus} />
                </span>
                <input
                  type="text"
                  value={shippingLine}
                  onChange={e => setShippingLine(e.target.value)}
                  placeholder="Carrier name"
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400">Carrier Code</span>
                <input
                  type="text"
                  value={carrierCode}
                  onChange={e => setCarrierCode(e.target.value.toUpperCase())}
                  onBlur={() => { if (carrierStatus === 'needs_code') syncCarrierCode(carrierMatch?.id || '', carrierCode) }}
                  placeholder="e.g. COSU"
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
                {carrierStatus === 'needs_code' && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                    Carrier "{shippingLine}" is on file but has no code yet — enter it above.
                  </p>
                )}
                {carrierStatus === 'new' && (
                  <p className="text-[10px] text-blue-500 dark:text-blue-400 mt-0.5">
                    New carrier — added when the job is created.
                  </p>
                )}
              </label>

              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 flex-wrap">
                  Departure Port <StatusBadge status={departureStatus} />
                  <PortLocodeWarning
                    port={departurePortMatch}
                    onUpdated={(updated) => {
                      setDeparturePortMatch(updated)
                      setDeparturePortCode(portIdentifier(updated, isSeaport))
                    }}
                  />
                </span>
                <input
                  type="text"
                  value={departurePort}
                  onChange={e => setDeparturePort(e.target.value)}
                  placeholder="Port name"
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400">Departure Port {isSeaport ? 'LOCODE' : 'IATA'}</span>
                <input
                  type="text"
                  value={departurePortCode}
                  onChange={e => { setDeparturePortCode(e.target.value.toUpperCase()); if (departureStatus !== 'needs_code') setDeparturePortId('') }}
                  onBlur={() => { if (departureStatus === 'needs_code') syncPortIdentifier(departurePortId, departurePortCode) }}
                  placeholder={isSeaport ? 'e.g. GBFXT' : 'e.g. LHR'}
                  maxLength={isSeaport ? 5 : 3}
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
                {departureStatus === 'needs_code' ? (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                    Port "{departurePort}" exists but has no {isSeaport ? 'LOCODE' : 'IATA code'} yet — enter it above.
                  </p>
                ) : departureStatus === 'new' ? (
                  <p className="text-[10px] text-blue-500 dark:text-blue-400 mt-0.5">
                    This is a new port — enter its {isSeaport ? '5-char UN/LOCODE' : '3-char IATA code'}; it'll be added when the job is created.
                  </p>
                ) : (
                  <p className="text-[10px] text-gray-400 mt-0.5">{isSeaport ? '5-char UN/LOCODE' : '3-letter IATA code'}</p>
                )}
              </label>

              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 flex-wrap">
                  Arrival Port <StatusBadge status={arrivalStatus} />
                  <PortLocodeWarning
                    port={arrivalPortMatch}
                    onUpdated={(updated) => {
                      setArrivalPortMatch(updated)
                      setArrivalPortCode(portIdentifier(updated, isSeaport))
                    }}
                  />
                </span>
                <input
                  type="text"
                  value={arrivalPort}
                  onChange={e => setArrivalPort(e.target.value)}
                  placeholder="Port name"
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400">Arrival Port {isSeaport ? 'LOCODE' : 'IATA'}</span>
                <input
                  type="text"
                  value={arrivalPortCode}
                  onChange={e => { setArrivalPortCode(e.target.value.toUpperCase()); if (arrivalStatus !== 'needs_code') setArrivalPortId('') }}
                  onBlur={() => { if (arrivalStatus === 'needs_code') syncPortIdentifier(arrivalPortId, arrivalPortCode) }}
                  placeholder={isSeaport ? 'e.g. GBFXT' : 'e.g. LHR'}
                  maxLength={isSeaport ? 5 : 3}
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
                {arrivalStatus === 'needs_code' ? (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                    Port "{arrivalPort}" exists but has no {isSeaport ? 'LOCODE' : 'IATA code'} yet — enter it above.
                  </p>
                ) : arrivalStatus === 'new' ? (
                  <p className="text-[10px] text-blue-500 dark:text-blue-400 mt-0.5">
                    This is a new port — enter its {isSeaport ? '5-char UN/LOCODE' : '3-char IATA code'}; it'll be added when the job is created.
                  </p>
                ) : (
                  <p className="text-[10px] text-gray-400 mt-0.5">{isSeaport ? '5-char UN/LOCODE' : '3-letter IATA code'}</p>
                )}
              </label>

              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400">Departure Date</span>
                <input
                  type="date"
                  value={departureDate}
                  onChange={e => setDepartureDate(e.target.value)}
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400">Arrival Date</span>
                <input
                  type="date"
                  value={arrivalDate}
                  onChange={e => setArrivalDate(e.target.value)}
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
              </label>

              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400">Created By</span>
                <select
                  value={createdBy}
                  onChange={e => setCreatedBy(e.target.value)}
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                >
                  {currentUserEmail && !userOptions.includes(currentUserEmail) && (
                    <option value={currentUserEmail}>{currentUserEmail}</option>
                  )}
                  {userOptions.map(email => (
                    <option key={email} value={email}>{email}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400">Shed</span>
                <select
                  value={shedId}
                  onChange={e => setShedId(e.target.value)}
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                >
                  <option value="">— None —</option>
                  {shedOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <label className="block col-span-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">Storage Location</span>
                <input
                  type="text"
                  value={storageLocation}
                  onChange={e => setStorageLocation(e.target.value)}
                  placeholder="Optional"
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
              </label>

              {/* Y-followup (task 22, bug F): applied to the whole batch, same as Carrier/
                  Ports above -- create-autojobs falls back to these master-level values for
                  every child job that doesn't have its own per-HBL selection. */}
              <label className="block col-span-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">Customer<span className="text-red-500 ml-0.5">*</span></span>
                <SearchableSelect
                  options={customers.map(c => ({ id: c.id, label: `${c.code} - ${c.name}` }))}
                  value={invoiceToId}
                  onChange={setInvoiceToId}
                  allowCreate={false}
                  placeholder="Search invoice recipient..."
                />
                {confirmAttempted && !invoiceToId && (
                  <p className="text-xs text-red-500 mt-1">Customer is required</p>
                )}
              </label>
              <label className="block col-span-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">Overseas Agent<span className="text-red-500 ml-0.5">*</span></span>
                <SearchableSelect
                  options={customers.map(c => ({ id: c.id, label: `${c.code} - ${c.name}` }))}
                  value={overseasAgentId}
                  onChange={setOverseasAgentId}
                  allowCreate={false}
                  placeholder="Search overseas agent..."
                />
                {confirmAttempted && !overseasAgentId && (
                  <p className="text-xs text-red-500 mt-1">Overseas Agent is required</p>
                )}
              </label>
              <label className="block col-span-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">Port Agent</span>
                <SearchableSelect
                  options={customers.map(c => ({ id: c.id, label: `${c.code} - ${c.name}` }))}
                  value={portAgentId}
                  onChange={setPortAgentId}
                  allowCreate={false}
                  placeholder="Search port agent..."
                />
              </label>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={true}
                disabled
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-900 dark:text-white font-medium">
                Create jobs in system
              </span>
            </label>

            {BOXTOP_SYNC_ENABLED && <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={syncToBoxtrax}
                onChange={e => setSyncToBoxtrax(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Sync to BoxTop
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Send job data to BoxTop platform</p>
              </div>
            </label>}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmClick}
              disabled={isLoading || hasMissingCodes}
              title={hasMissingCodes ? 'Fill in the missing port/carrier code(s) above first' : undefined}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="inline-block animate-spin">⏳</span>
                  Creating...
                </>
              ) : (
                '✓ Confirm & Create'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
